import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState } from "react";
import ModuleHeader from "./ModuleHeader";
import ReactEcharts from "echarts-for-react";
import DateDropdown from "@/components/DateDropdown";
import { REM_CONFIG } from "@/utils/fontSize";
import moment from "moment";
// ECharts 配置（已修复所有警告）
const getEchartsOption = (data) => {
    const htmlFontSize = 100;
    return {
        backgroundColor: "transparent",
        title: {
            text: "合计数:26259.86(万元)",
            left: "center",
            top: 20, // 标题紧凑
            textStyle: {
                fontSize: REM_CONFIG.fontSize.normal,
                color: "#fff",
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
            backgroundColor: "rgba(7, 31, 75, 0.8)",
            borderColor: "#446b9f",
            textStyle: { color: "#fff" },
            formatter: (params) => {
                const category = params[0]?.name;
                if (!category)
                    return "";
                const targetItem = data.find((item) => item.category === category);
                if (!targetItem)
                    return "";
                return `
          <div style="color:#fff;margin-bottom:4px">${targetItem.category}</div>
          <div style="margin-bottom:2px">本年价值：${targetItem.currentYearValue} 万元</div>
          <div style="margin-bottom:2px">上年价值：${targetItem.lastYearValue} 万元</div>
          <div>较上年：${targetItem.yoyPercentage}%</div>
        `;
            },
        },
        // ✅ 修复1：删除多余的"总价值()"，只保留实际存在的系列
        legend: {
            data: ["本年价值", "上年价值", "较上年价值"],
            textStyle: {
                color: "#FFFFFF",
                fontSize: REM_CONFIG.fontSize.small,
            },
            itemGap: 15,
            top: 0,
            left: "center",
        },
        xAxis: {
            type: "category",
            data: data.map((item) => item.category),
            axisLabel: {
                color: "#FFFFFF",
                fontSize: REM_CONFIG.fontSize.small,
                interval: 0,
            },
            axisTick: { show: false },
        },
        yAxis: [
            {
                type: "value",
                name: "金额（万元）",
                nameTextStyle: {
                    fontSize: REM_CONFIG.fontSize.small,
                    color: "#FFFFFF",
                    align: "center",
                },
                nameLocation: "end",
                axisLabel: {
                    color: "#FFFFFF",
                    fontSize: REM_CONFIG.fontSize.small,
                },
                axisTick: { show: false },
                splitLine: {
                    lineStyle: { color: "rgba(255,255,255,0.1)", type: "dashed" },
                },
            },
            {
                type: "value",
                name: "百分比(%)",
                nameTextStyle: {
                    fontSize: REM_CONFIG.fontSize.small,
                    color: "#FFFFFF",
                    padding: [0, 0, 0, 40],
                },
                axisLabel: {
                    color: "#FFFFFF",
                    fontSize: REM_CONFIG.fontSize.small,
                },
                axisTick: { show: false },
                splitLine: { show: false },
            },
        ],
        series: [
            {
                name: "本年价值",
                type: "bar",
                data: data.map((item) => item.currentYearValue),
                itemStyle: {
                    // ✅ 修复2：barBorderRadius → borderRadius（废弃API替换）
                    borderRadius: REM_CONFIG.barBorderRadius,
                    color: {
                        type: "linear",
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: "#1099fb" },
                            { offset: 1, color: "#1b6eaa" },
                        ],
                    },
                },
                barWidth: "15%",
            },
            {
                name: "上年价值",
                type: "bar",
                data: data.map((item) => item.lastYearValue),
                itemStyle: {
                    // ✅ 修复2：barBorderRadius → borderRadius
                    borderRadius: REM_CONFIG.barBorderRadius,
                    color: {
                        type: "linear",
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: "#afffff" },
                            { offset: 1, color: "#1cffff" },
                        ],
                    },
                },
                barWidth: "15%",
            },
            {
                name: "较上年价值",
                type: "line",
                yAxisIndex: 1,
                data: data.map((item) => item.yoyPercentage),
                itemStyle: { color: "#ffd850" },
                lineStyle: {
                    color: "#ffd850",
                    width: 0.02 * htmlFontSize,
                },
                symbol: "circle",
                symbolSize: 0.06 * htmlFontSize,
            },
        ],
        grid: {
            left: "6%",
            right: "8%",
            bottom: `${0.06 * htmlFontSize}px`,
            top: `${0.45 * htmlFontSize}px`, // 标题图表间距紧凑
            containLabel: true,
        },
    };
};
// 核心组件
const AssetValueRatio = ({ className = "", initDate = moment().format("YYYY-MM"), }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [chartData, setChartData] = useState([
        {
            category: "固资",
            currentYearValue: 7000,
            lastYearValue: 4500,
            yoyPercentage: 65,
        },
        {
            category: "低耗",
            currentYearValue: 9500,
            lastYearValue: 8500,
            yoyPercentage: 60,
        },
        {
            category: "物资",
            currentYearValue: 6200,
            lastYearValue: 8000,
            yoyPercentage: 55,
        },
        {
            category: "三剂",
            currentYearValue: 3800,
            lastYearValue: 5000,
            yoyPercentage: 60,
        },
    ]);
    const [selectedDate, setSelectedDate] = useState(initDate);
    // 数据请求逻辑
    const fetchChartData = async (date) => {
        try {
            setSelectedDate(date);
            setTimeout(() => {
                const mockNewData = [
                    {
                        category: "固资",
                        currentYearValue: 25075,
                        lastYearValue: 22000,
                        yoyPercentage: 13.68,
                    },
                    {
                        category: "利旧",
                        currentYearValue: 765.37,
                        lastYearValue: 720.21,
                        yoyPercentage: 6.27,
                    },
                    {
                        category: "物资",
                        currentYearValue: 104.29,
                        lastYearValue: 100,
                        yoyPercentage: 4.29,
                    },
                    {
                        category: "三剂",
                        currentYearValue: 217.57,
                        lastYearValue: 200,
                        yoyPercentage: 8.79,
                    },
                    {
                        category: "低耗",
                        currentYearValue: 97.63,
                        lastYearValue: 92,
                        yoyPercentage: 6.12,
                    },
                ];
                setChartData(mockNewData);
            }, 800);
        }
        catch (error) {
            console.error("资产价值数据请求失败：", error);
        }
    };
    // 初始化+自适应
    useEffect(() => {
        fetchChartData(initDate);
        if (!containerRef.current || !chartRef.current)
            return;
        const resizeObserver = new ResizeObserver((entries) => {
            const htmlFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 100;
            requestAnimationFrame(() => {
                entries.forEach((entry) => {
                    const { width, height } = entry.contentRect;
                    chartRef.current?.getEchartsInstance()?.resize({
                        width,
                        height: height - 0.4 * htmlFontSize,
                    });
                });
            });
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [initDate]);
    // 数据更新重绘图表
    useEffect(() => {
        if (chartRef.current) {
            chartRef.current
                .getEchartsInstance()
                .setOption(getEchartsOption(chartData));
        }
    }, [chartData]);
    return (_jsxs("div", { ref: containerRef, className: `
        relative 
        bg-gradient-to-t 
        from-[rgba(7,31,75,0.5)] 
        to-[rgba(6,32,66,0.2)] 
        flex flex-col 
        ${className}
      `, children: [_jsx(ModuleHeader, { title: "\u8D44\u4EA7\u5927\u7C7B\u4EF7\u503C\u5360\u6BD4", children: _jsx(DateDropdown, { initDate: initDate, onDateChange: (date) => fetchChartData(date) }) }), _jsx("div", { className: "flex-1 min-h-0 w-full", children: _jsx(ReactEcharts, { ref: chartRef, option: getEchartsOption(chartData), style: { width: "100%", height: "100%" }, notMerge: true, lazyUpdate: true }) })] }));
};
export default AssetValueRatio;
